import {createContext, useEffect, useMemo, useRef} from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import _cloneDeep from 'lodash/cloneDeep';
import buildActionLookup from './utils/action-lookup';
import getPreviewBaseUrl from './utils/preview-domain';

import Theme from "./theme";
import BlockList from "./blocks";
import SiteNavigationBlock from "./blocks/site-navigation-block/";
import FooterNavigationBlock from "./blocks/footer-navigation-block";

import SingleColumn from "./layouts/single-column";
import MultiColumn from "./layouts/multi-column";
import Blog from "./layouts/blog";
import HashScrollQuerySync from "./components/hash-scroll-query-sync";
import PopupBlock from "./blocks/popup-block";
import WebsiteConnectionNotice from './realtime/website-connection-notice';

export const WebsiteContext = createContext({
    cdnUrl: '',
    fileDownloadUrl: '',
    captchaProvider: 'cap',
    hcaptchaSiteKey: '',
    capSiteKey: '',
    capApiEndpoint: '',
    honeypotEnabled: true,
    builder: false,
    breakpoint: 640,
    picker: false,
    env: '',
    channel: '',
    node: '',
    customerSession: null,
    reviewsSetup: {
        google: 'unknown'
    },
    reviewsSetupLoading: {
        google: false
    },
    onRecheckGoogleReviewsSetup: () => {}
});

Website.propTypes = {
    className: PropTypes.string,
    cdnUrl: PropTypes.string.isRequired,
    fileDownloadUrl: PropTypes.string.isRequired,
    captchaProvider: PropTypes.string,
    hcaptchaSiteKey: PropTypes.string,
    capSiteKey: PropTypes.string,
    capApiEndpoint: PropTypes.string,
    honeypotEnabled: PropTypes.bool,
    env: PropTypes.string.isRequired,
    channel: PropTypes.string,
    node: PropTypes.object,
    builder: PropTypes.bool,
    picker: PropTypes.bool,
    header: PropTypes.shape({
        title: PropTypes.string.isRequired,
        items: PropTypes.array.isRequired,
    }),
    footer: PropTypes.shape({
        items: PropTypes.array.isRequired,
    }),
    theme: PropTypes.object.isRequired,
    blocks: PropTypes.array.isRequired,
    blockActions: PropTypes.bool,
    onBlockAction: PropTypes.func,
    onBlockSelected: PropTypes.func,
    selectedBlock: PropTypes.string,
    onClick: PropTypes.func,
};

Website.defaultProps = {
    blockActions: false,
    onBlockAction: () => {},
    breakpoint: 640
};

export default function Website(
    {
        className,
        header,
        footer,
        social,
        unlinked,
        productCategories,
        schema,
        theme,
        blocks,
        blockActions,
        isEditing,
        onBlockAction,
        onBlockSelected,
        selectedBlock,
        onClick,
        children,
        rootPage,
        isDesign,
        isNavigation,
        actionLookup,
        popups,
        ...props
    }
) {
    const pageRef = useRef();
    // Build a lookup of all nodes that we can navigate to
    const lookup = useMemo(() => buildActionLookup({
        header,
        footer,
        unlinked,
        productCategories,
        actionLookup
    }), [header, footer, unlinked, productCategories, actionLookup]);

    // The rest of the props are for the website context
    // `props` is reconstructed on every render. Keeping this composite object in
    // a memo with a partial dependency list leaves consumers with stale channel,
    // website, builder, schema, and other values while the builder is updating.
    const context = {
        ..._cloneDeep(props),
        blocks,
        theme,
        isEditing,
        rootPage,
        schema,
        isDesign,
        actionLookup: lookup
    };

    useEffect(() => {
        const footerHeight = document.getElementById("block_footer")?.offsetHeight
        document.documentElement.style.setProperty('--footer-height', `${footerHeight}px`);
    }, [blocks])

    let PageLayout = SingleColumn;

    if (props.node?.nodeType === 'blog') {
        PageLayout = Blog;
    } else if (rootPage && (props.node?.nodeType !== 'folder' || props.node.nodeType !== 'action')) {
        PageLayout = MultiColumn;
    }

    return (
        <div ref={pageRef}
             id="vert-app"
             className={classNames("h-full preview mx-auto flex flex-col",
                 `heading_${context.theme.selectedFont.headingName.split(' ').join('_').toLowerCase()}`,
                 `body_${context.theme.selectedFont.bodyName.split(' ').join('_').toLowerCase()}`,
                 context.builder && !isNavigation ? "min-h-[calc(100vh-var(--footer-height))] sm:min-h-[calc(100vh-var(--default-app-bar-height)-var(--footer-height))]" : "min-h-screen",
                 context.builder && isNavigation ? "min-h-[100px]" : null,
                 className
             )}
             onClick={onClick}>
            <HashScrollQuerySync/>
            {popups && <PopupBlock popups={popups}/>}
            <Theme settings={theme}/>
            <WebsiteContext.Provider value={context}>
                {header && !["checkout"].includes(props?.pageType) ? (
                    <SiteNavigationBlock actions={blockActions} onAction={onBlockAction} {...header}
                                         settings={theme.header}/>
                ) : null}
                <PageLayout>
                    {blocks.length > 0 ? (
                        <BlockList
                            pageRef={pageRef}
                            blocks={blocks}
                            actions={blockActions}
                            onAction={onBlockAction}
                            onSelect={onBlockSelected}
                            selectedBlock={selectedBlock}
                            websiteContext={context}
                        />
                    ) : context.builder ? (
                        <>
                            {!isNavigation ?
                                <div
                                    className={"flex flex-col items-center justify-center text-center placeholder-page relative grow p-6"}>
                                    <h4 className={"reset-font !font-bold"}>Create a new page using Page Assistant or
                                        start
                                        with a blank page or template</h4>
                                </div>
                                : <></>
                            }
                        </>
                    ) : null}
                </PageLayout>
                {footer && !["checkout"].includes(props?.pageType) ? (
                    <FooterNavigationBlock actions={blockActions} onAction={onBlockAction} id="footer"
                                           hideInlineAddBlockButton={true} {...footer} settings={theme.footer}
                                           baseColour={theme.baseColour}
                                           providerBrand={props.providerBrand ?? props.website?.providerBrand}
                                           artworkOrigin={props.builder ? getPreviewBaseUrl(props.website?.previewDomain) : undefined}/>
                ) : null}
                {children}
                {!context.builder && <WebsiteConnectionNotice />}
            </WebsiteContext.Provider>
        </div>
    );
};
